import { useCallback, useEffect, useId, useMemo, useRef, useState, type FormEvent } from 'react';
import { FormErrorSummary } from '@/components/catalog/FormShell';
import { without } from '@/components/catalog/form-utils';
import { ApiError, api } from '@/lib/api';
import type {
  Product,
  ProductCreateInput,
  ProductUpdateInput,
  ProductXeroPush,
} from '@/lib/types';
import { Button, Checkbox, Combobox, Field, Input, Modal, Select } from '@/components/ui';
import type { ComboboxOption } from '@/components/ui';
import { ProductXeroBadge } from '@/components/products/ProductXeroBadge';

export interface ProductFormModalProps {
  open: boolean;
  onClose: () => void;
  /** Omitted = create mode. Provided = edit mode. */
  product?: Product;
  /**
   * Called with the saved record so the caller can refresh in place, and with
   * the server's Xero Item push report (absent on an older API).
   */
  onSaved: (product: Product, xeroPush?: ProductXeroPush) => void;
}

/*
 * Server-enforced max lengths, mirrored so the control stops the user at the
 * same place the API would.
 */
const MAX_LENGTHS = {
  productName: 255,
  productCode: 50,
  productOwnerId: 64,
  productOwner: 150,
  productCategory: 150,
  unitLabel: 40,
  xeroSalesAccountCode: 10,
} as const;

/** Everything except productName clears to null when blanked. */
const NULLABLE_TEXT_KEYS = [
  'productCode',
  'productOwnerId',
  'productOwner',
  'productCategory',
  'xeroSalesAccountCode',
] as const;

type TextKey = keyof typeof MAX_LENGTHS;

const TEXT_KEYS = Object.keys(MAX_LENGTHS) as TextKey[];

/**
 * The API accepts a number or a string but validates the *shape*: up to ten
 * digits, then at most two decimal places. 18000.567 is a 422, not a silent
 * round, so the same rule runs here.
 */
const PRICE_PATTERN = /^\d{1,10}(\.\d{1,2})?$/;

/**
 * The recommended discount's shape: up to twelve digits, then at most two
 * decimal places.
 *
 * ⚠️ **NOT `PRICE_PATTERN`, and the two must never be unified.** They differ in
 * two ways, both deliberate. The column is `DECIMAL(14,2)` against the price's
 * `DECIMAL(12,2)`, because it is pinned to the line `discount_amount` it seeds
 * rather than to the price it comes off. And it is **UNSIGNED**: a LINE's
 * `unitPrice` gained a leading `-` in migration 171 for deduction lines, and
 * carrying that relaxation across to here would make a discount a surcharge.
 * One character apart, opposite meanings.
 */
const DISCOUNT_PATTERN = /^\d{1,12}(\.\d{1,2})?$/;

/** One page of the catalogue. `/products` caps `perPage` at 100. */
const PRODUCT_OPTION_PER_PAGE = 100;

/** Every value is a string: '' is "blank", which maps to null (or omission). */
interface FormValues {
  productName: string;
  productCode: string;
  productOwnerId: string;
  productOwner: string;
  productCategory: string;
  /** 'true' | 'false' — a native select, so never genuinely empty. */
  productActive: string;
  unitPrice: string;
  /**
   * What a line selling this product should start its discount at, as an exact
   * decimal string.
   *
   * ⚠️ **`''` is NO RECOMMENDATION and `'0.00'` is a recommendation of nothing
   * off** — the same distinction `unitPrice` draws between unpriced and free,
   * and the same `?? 0` that would destroy it. A blank box therefore clears the
   * column rather than meaning "leave it alone", which is why it does not fall
   * through to the `qtyOrdered` treatment two lines down.
   */
  recommendedDiscountAmount: string;
  /**
   * Lines must sell a whole multiple of this (owner, 2026-10-16). `''` = any
   * quantity, which clears to `null` on an edit and is omitted on create.
   */
  quantityStep: string;
  /** What one unit is called, singular ("license"). `''` clears to `null`. */
  unitLabel: string;
  qtyOrdered: string;
  /**
   * The travel product's id as a string — `''` is "none", which clears.
   *
   * ⚠️ Held as a string like every other value here because `changedKeys`
   * compares `values[key].trim()` across the whole object; a number would fail
   * at `tsc`, which is the intended guard rather than a nuisance.
   */
  travelProductId: string;
  /**
   * `'true' | 'false'` — a tickbox, so never genuinely empty, and held as a
   * string for the same `changedKeys` reason as `travelProductId` above.
   *
   * **WHAT TICKING IT DOES:** lines selling this product stop appearing as
   * their own rows on the Client Deliverables Tracker. It is not a category and
   * must not be worded as one — nothing about the product's price, catalogue
   * position or Xero mapping changes, and the only observable effect is rows
   * absent from one screen.
   *
   * ⚠️ **That consequence is silent and runs in the vanishing direction**, which
   * is why it is stated here rather than beside the control: a wrongly ticked
   * product simply produces a shorter tracker, and a shorter page reads as work
   * nobody has quoted. The no-helper-text prohibition forbids rendering this
   * sentence under the tickbox; a refusal or a why-this-is-disabled line would
   * survive, a general explanation does not.
   *
   * ⚠️ **It replaces a `': Add-ons'` match on the admin-editable product NAME**,
   * which is the free-text-read-as-a-flag trap this codebase has been bitten by
   * three times — an ordinary rename switched the rule off with nothing failing.
   * So the flag must stay the only test, and this control is the only reason a
   * NEW add-on product needs no migration.
   *
   * ⚠️ **It is IN `FormValues`, unlike the Xero keys, and that is deliberate.**
   * `xeroProductId` / `xeroProductCode` are absent from this type so that no
   * payload this form can build is able to express them — they are owned by the
   * sync and refused on write, so the right guard is structural. `isAddOn` is
   * the opposite: an administrator is meant to set it, so it belongs on the
   * writable side of that line and picks up `KNOWN_WRITE_KEYS` routing with it.
   */
  isAddOn: string;
  /**
   * The Xero sales account the product's Xero Item posts to (owner 2026-10-28).
   * `''` clears to `null` on an edit and is omitted on create. Unlike
   * `xeroProductId` / `xeroProductCode` this one IS writable.
   */
  xeroSalesAccountCode: string;
}

/** The record as the form sees it. Also the baseline every diff is taken against. */
function toFormValues(product?: Product): FormValues {
  return {
    productName: product?.productName ?? '',
    productCode: product?.productCode ?? '',
    productOwnerId: product?.productOwnerId ?? '',
    productOwner: product?.productOwner ?? '',
    productCategory: product?.productCategory ?? '',
    // New products default to active: the column's server-side default is not
    // documented, so the create payload always states it rather than guessing.
    productActive: product ? String(product.productActive) : 'true',
    unitPrice: product?.unitPrice ?? '',
    // `?? ''` maps only the NULL — a stored '0.00' stays '0.00' and reads back
    // out as '0.00', so "nothing off" is never quietly turned into "nothing set".
    recommendedDiscountAmount: product?.recommendedDiscountAmount ?? '',
    // `null` and ABSENT (an older API) both read as blank, and blank on an
    // untouched form is never sent, so an older API is never handed the key.
    quantityStep:
      product?.quantityStep === null || product?.quantityStep === undefined
        ? ''
        : String(product.quantityStep),
    unitLabel: product?.unitLabel ?? '',
    qtyOrdered: product ? String(product.qtyOrdered) : '',
    travelProductId: product?.travelProduct ? String(product.travelProduct.id) : '',
    // Unticked on create. ⚠️ Unlike `productActive` above, the create payload
    // does NOT then state it — see `buildPayload`; the two look alike and are
    // decided differently on purpose.
    isAddOn: product ? String(product.isAddOn) : 'false',
    // `null` and ABSENT (an older API) both read as blank; blank on an
    // untouched form is never sent.
    xeroSalesAccountCode: product?.xeroSalesAccountCode ?? '',
  };
}

/**
 * The `path` values a 422 may name that this form can actually MARK.
 *
 * ⚠️ **These are the keys the form SENDS**, which is what a 422's `path` names;
 * `toFormValues()` is keyed on exactly those, so deriving the list from it
 * keeps it from drifting away from the controls.
 *
 * It also inherits this file's structural guard for free: the Xero keys are
 * deliberately absent from `FormValues`, so they are absent here too — a 422
 * naming one of them is reported at form level rather than marking a box this
 * form does not render. That is the point of the split. A path can be non-empty
 * and still bind to nothing, and that case is the worse one because it looks
 * bindable: routed by path, the message lands on an input that does not exist,
 * disappears from the banner, and still inflates the "fields need attention"
 * count.
 */
const KNOWN_WRITE_KEYS: readonly string[] = Object.keys(toFormValues());

/**
 * Create/edit dialog for a product.
 *
 * The inner form is remounted per opening (and per record) so the fields always
 * start from the record rather than from whatever the last session left behind.
 */
export function ProductFormModal({ open, onClose, product, onSaved }: ProductFormModalProps) {
  if (!open) return null;
  return (
    <ProductForm key={product?.id ?? 'new'} onClose={onClose} product={product} onSaved={onSaved} />
  );
}

function ProductForm({
  onClose,
  product,
  onSaved,
}: {
  onClose: () => void;
  product?: Product;
  onSaved: ProductFormModalProps['onSaved'];
}) {
  const formId = useId();
  // Resolved from the wrapper so the dialog opens focus on the name input
  // rather than on its close button. `Input` takes no ref of its own.
  const nameRef = useRef<HTMLElement | null>(null);
  const isEdit = product !== undefined;

  const baseline = useMemo(() => toFormValues(product), [product]);
  const [values, setValues] = useState<FormValues>(baseline);
  const [clientErrors, setClientErrors] = useState<Record<string, string>>({});
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  /*
   * ── The travel-product picker's options ─────────────────────────────────
   *
   * Searched server-side, exactly like the product picker on
   * `DeliverableFormModal`: `/products` matches `search` on `product_name`
   * only and caps `perPage` at 100, so a page is a page and the footnote says
   * so rather than letting the 101st product read as non-existent.
   *
   * ⚠️ **`active: true`, and the stored value is pinned back below.** Those are
   * the two halves of the deactivation trap and NEITHER works alone. The server
   * refuses a retired id on write — *"…is retired and cannot be named as a
   * travel product"* — so an unfiltered list turns every retired product into an
   * ordinary, indistinguishable menu row whose only outcome is a 422, the same
   * complaint this file makes about self-selection two paragraphs down. Filtering
   * alone is no better: it drops the record's OWN stored travel product out of
   * the list, the trigger falls back to the placeholder, and the next save reads
   * as a deliberate clearing. So: fetch the offerable rows, then put the stored
   * one back, marked as no longer offered when that is provable.
   *
   * ⚠️ A failed read is NOT an empty catalogue, and the two must not look
   * alike: `failed` drives its own message, and the list is never silently
   * offered as "there is nothing to choose".
   */
  const [travelCandidates, setTravelCandidates] = useState<Product[]>([]);
  const [travelMeta, setTravelMeta] = useState({ truncated: false, total: 0, failed: false });
  const [travelSearch, setTravelSearch] = useState('');
  /** The term the last settled fetch was for, so "loading" is derived at render. */
  const [travelLoaded, setTravelLoaded] = useState<string | null>(null);
  /**
   * The option the user has CHOSEN in this session, kept out of the fetch.
   *
   * ⚠️ Without it the trigger goes blank on a value that is still held and will
   * still be sent. `Combobox` resolves its trigger label purely from `options`,
   * and **re-opening the popover fires `onSearch('')`**, which refetches
   * unfiltered page one — so a product chosen from page three is gone from
   * `options` the moment the list is reopened, the trigger falls back to the
   * placeholder, and the placeholder here reads *"No travel product"*, which is
   * the same string as `emptyOptionLabel`. The reader is told the field is empty
   * while `buildPayload` sends the id. The stored pin below does not cover this:
   * it only ever holds the record's ORIGINAL value, and on create there is none.
   */
  const [travelSelectionPin, setTravelSelectionPin] = useState<ComboboxOption | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    api
      .listProducts(
        {
          perPage: PRODUCT_OPTION_PER_PAGE,
          // Only rows that can actually be saved. A retired id is a 422 on write.
          active: true,
          // Blank keys are omitted, never sent empty: `?search=` is a 422
          // server-side rather than "no filter".
          ...(travelSearch ? { search: travelSearch } : {}),
        },
        controller.signal,
      )
      .then((result) => {
        if (controller.signal.aborted) return;
        setTravelCandidates(result.data);
        setTravelMeta({
          truncated: result.meta.totalPages > 1,
          total: result.meta.total,
          failed: false,
        });
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setTravelCandidates([]);
        setTravelMeta({ truncated: false, total: 0, failed: true });
      })
      .finally(() => {
        if (!controller.signal.aborted) setTravelLoaded(travelSearch);
      });
    return () => controller.abort();
  }, [travelSearch]);

  // Stable, or `Combobox`'s debounce would restart every render and never fire.
  const searchTravelProducts = useCallback((term: string) => setTravelSearch(term), []);

  /**
   * ⚠️ **The record being edited is EXCLUDED, and that is the whole point of
   * this memo.** A product cannot be its own travel product: the server refuses
   * it, so offering it is offering a choice whose only outcome is a 422 naming
   * a field the user had every reason to think they had filled in correctly.
   * The filter is on `id`, never on the name — two products may share one.
   *
   * On CREATE there is no id yet, so nothing is excluded and nothing needs to
   * be: the record cannot appear in a list it is not in.
   */
  const travelPageOptions = useMemo<ComboboxOption[]>(
    () =>
      travelCandidates
        .filter((candidate) => candidate.id !== product?.id)
        .map((candidate) => ({
          value: String(candidate.id),
          // `productName`, not `.name` — the catalog keys differ from the CRM ones.
          label: candidate.productName,
          hint: candidate.productCode ?? undefined,
        })),
    [travelCandidates, product],
  );

  /**
   * Is the stored travel product PROVABLY retired?
   *
   * ⚠️ Absence from an `active: true` page proves that only when the page IS the
   * whole active catalogue — an unfiltered, settled, un-truncated read that did
   * not fail. Under a search term, on page one of many, or after a failed read,
   * absence says nothing whatever, and `withStoredOption()` in
   * `use-reference-options.ts` withholds the same label for the same reason.
   * Marking a live product "no longer offered" is a claim the reader acts on, so
   * it is made only where it is true.
   *
   * The embedded `travelProduct` is `{id, productName}` and carries no
   * `productActive`, so this cross-reference is the only signal there is.
   */
  const storedTravelKnownRetired = useMemo(() => {
    const stored = product?.travelProduct;
    if (!stored) return false;
    if (travelMeta.failed || travelMeta.truncated) return false;
    if (travelSearch !== '' || travelLoaded !== '') return false;
    return !travelCandidates.some((candidate) => candidate.id === stored.id);
  }, [product, travelCandidates, travelMeta, travelSearch, travelLoaded]);

  const travelProductOptions = useMemo<ComboboxOption[]>(() => {
    const stored = product?.travelProduct;
    const storedPin: ComboboxOption | null = stored
      ? {
          value: String(stored.id),
          label: stored.productName,
          ...(storedTravelKnownRetired ? { hint: 'No longer offered' } : {}),
        }
      : null;

    /*
     * ⚠️ **TWO pins, and they are different values.** The record's original, so
     * a retired-but-stored association still renders and a mis-click is
     * revertible without a search; and whatever the user has chosen SINCE, which
     * the stored pin can never cover and which create mode has no other source
     * for. Both must survive a refetch — reopening the popover refetches page
     * one, and an unpinned selection disappears from `options` there.
     */
    const pinned: ComboboxOption[] = [];
    for (const pin of [travelSelectionPin, storedPin]) {
      if (!pin) continue;
      if (pinned.some((existing) => existing.value === pin.value)) continue;
      if (travelPageOptions.some((option) => option.value === pin.value)) continue;
      pinned.push(pin);
    }
    return [...pinned, ...travelPageOptions];
  }, [travelPageOptions, product, storedTravelKnownRetired, travelSelectionPin]);

  const travelLoading = travelLoaded !== travelSearch;

  // Server messages win: they are the authority on what the API rejected.
  const errors = useMemo(() => ({ ...clientErrors, ...serverErrors }), [clientErrors, serverErrors]);

  /*
   * The diff. Comparing trimmed values means a stray space never counts as a
   * change, so an edit that touched nothing real still sends nothing at all.
   */
  const changedKeys = useMemo(() => {
    const keys = Object.keys(baseline) as (keyof FormValues)[];
    return keys.filter((key) => values[key].trim() !== baseline[key].trim());
  }, [values, baseline]);
  const isDirty = changedKeys.length > 0;

  function setField(key: keyof FormValues, next: string) {
    setValues((prev) => ({ ...prev, [key]: next }));
    // An error about a value the user has since changed is noise, not guidance.
    setClientErrors((prev) => (key in prev ? without(prev, key) : prev));
    setServerErrors((prev) => (key in prev ? without(prev, key) : prev));
  }

  /*
   * PATCH semantics: an omitted key is left untouched, an explicit null clears
   * the field. So the edit payload carries *only* what changed — spreading the
   * whole record would also send the read-only Xero columns and 422 outright.
   * Create takes the opposite tack: send what was filled in, omit the rest.
   */
  function buildPayload(): ProductCreateInput | ProductUpdateInput {
    if (product) {
      const payload: ProductUpdateInput = {};
      for (const key of changedKeys) {
        const next = values[key].trim();
        if (key === 'productName') {
          payload.productName = next;
        } else if (key === 'productActive') {
          payload.productActive = next === 'true';
        } else if (key === 'unitPrice') {
          // null and '' both clear the price server-side; null is the explicit one.
          payload.unitPrice = next === '' ? null : next;
        } else if (key === 'recommendedDiscountAmount') {
          /*
           * Blank is a real instruction here, unlike `qtyOrdered` below: an
           * explicit null WITHDRAWS the recommendation, and it only reaches this
           * branch because the value changed, so that is what was asked for.
           *
           * ⚠️ Sent as the STRING it was typed as. `Number()` would round-trip
           * an exact decimal through a float, which is how '1234567890.15' stops
           * being itself — and the API takes the string.
           */
          payload.recommendedDiscountAmount = next === '' ? null : next;
        } else if (key === 'quantityStep') {
          // A real JSON integer; blank withdraws the step.
          payload.quantityStep = next === '' ? null : Number(next);
        } else if (key === 'qtyOrdered') {
          // The column is not nullable and the schema does not coerce, so a
          // blank box means "leave it alone" — there is no way to clear it.
          if (next !== '') payload.qtyOrdered = Number(next);
        } else if (key === 'travelProductId') {
          // Unlike `qtyOrdered`, blank here is a real instruction: an explicit
          // null CLEARS the association. It only reaches this branch because
          // the value changed, so clearing is what the user asked for.
          payload.travelProductId = next === '' ? null : Number(next);
        } else if (key === 'isAddOn') {
          /*
           * A real boolean, like `productActive` — the API accepts `Booleanish`,
           * but sending the form's own `'true'`/`'false'` string would make this
           * the one write here that leans on coercion.
           *
           * ⚠️ **An UNTOUCHED tickbox sends nothing, and that is the diff's job
           * rather than a check of its own**: this branch is only reached for a
           * key in `changedKeys`, which compares against the stored value. So
           * re-ticking a box back to where it started also sends nothing, and a
           * PATCH can never re-assert a flag nobody moved.
           */
          payload.isAddOn = next === 'true';
        } else {
          payload[key] = next === '' ? null : next;
        }
      }
      return payload;
    }

    const payload: ProductCreateInput = {
      productName: values.productName.trim(),
      productActive: values.productActive === 'true',
    };
    for (const key of NULLABLE_TEXT_KEYS) {
      const next = values[key].trim();
      if (next !== '') payload[key] = next;
    }
    const price = values.unitPrice.trim();
    if (price !== '') payload.unitPrice = price;
    // Omitted rather than null, for the same reason as the travel product: there
    // is nothing to clear on a record that does not exist yet.
    const discount = values.recommendedDiscountAmount.trim();
    if (discount !== '') payload.recommendedDiscountAmount = discount;
    // Omitted when blank: the schema is `.strict()`, so an API that has not
    // shipped these keys refuses them, and a blank has nothing to say anyway.
    const step = values.quantityStep.trim();
    if (step !== '') payload.quantityStep = Number(step);
    const unitLabel = values.unitLabel.trim();
    if (unitLabel !== '') payload.unitLabel = unitLabel;
    const qty = values.qtyOrdered.trim();
    if (qty !== '') payload.qtyOrdered = Number(qty);
    // Omitted rather than sent as null on create: there is nothing to clear on
    // a record that does not exist, and the write schema is `.strict()`, so
    // every key sent is a key that has to be accepted.
    const travel = values.travelProductId.trim();
    if (travel !== '') payload.travelProductId = Number(travel);
    /*
     * ⚠️ **Sent ONLY when ticked, which is NOT what `productActive` two dozen
     * lines up does — and the difference is deliberate rather than an
     * oversight somebody should harmonise.**
     *
     * `productActive` is always stated because the column's server-side default
     * is not documented, so omitting it would be a guess. This column's default
     * is known to be false: the migration that adds it seeds only the seven
     * products that were add-ons already, so "not an add-on" is exactly what an
     * absent key means. Stating it buys nothing.
     *
     * ⚠️ And it costs something real. The write schemas here are `.strict()`,
     * so a key the server has not shipped yet is a **422 naming it** — which on
     * create is a form-level banner over a product that was not created. Sending
     * `isAddOn: false` unconditionally would therefore break EVERY product
     * creation for as long as this client is ahead of the API, which is the
     * "order: server, then client" hazard. Sent only on a tick, the blast radius
     * is one administrator who deliberately asked for the new behaviour, and
     * ordinary creates keep working throughout.
     */
    if (values.isAddOn === 'true') payload.isAddOn = true;
    return payload;
  }

  function validate(): Record<string, string> {
    const found: Record<string, string> = {};

    if (values.productName.trim() === '') found.productName = 'Product name is required.';

    for (const key of TEXT_KEYS) {
      if (values[key].trim().length > MAX_LENGTHS[key]) {
        found[key] = `Must be ${MAX_LENGTHS[key]} characters or fewer.`;
      }
    }

    const price = values.unitPrice.trim();
    if (price !== '' && !PRICE_PATTERN.test(price)) {
      found.unitPrice = 'Up to 10 digits and 2 decimal places, e.g. 18000.00.';
    }

    const discount = values.recommendedDiscountAmount.trim();
    if (discount !== '' && !DISCOUNT_PATTERN.test(discount)) {
      /*
       * A leading '-' is already refused by the pattern, but "up to 12 digits"
       * does not tell the reader the box is unsigned — and typing a minus is the
       * natural way to say "take this off". Name the rule that was actually
       * broken, or the refusal reads as being about the length.
       */
      found.recommendedDiscountAmount = discount.startsWith('-')
        ? 'A discount cannot be negative. Enter the amount to take off, e.g. 2500.00.'
        : 'Up to 12 digits and 2 decimal places, e.g. 2500.00.';
    }

    const step = values.quantityStep.trim();
    if (step !== '') {
      if (!/^\d+$/.test(step) || Number(step) < 1) found.quantityStep = 'Whole number, 1 or more.';
      else if (!Number.isSafeInteger(Number(step))) found.quantityStep = 'That number is too large.';
    }

    const qty = values.qtyOrdered.trim();
    if (qty !== '') {
      if (!/^\d+$/.test(qty)) found.qtyOrdered = 'Whole number, 0 or more.';
      else if (!Number.isSafeInteger(Number(qty))) found.qtyOrdered = 'That number is too large.';
    }

    return found;
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();

    const found = validate();
    setClientErrors(found);
    setServerErrors({});
    setFormError(null);
    if (Object.keys(found).length > 0) return;

    const payload = buildPayload();
    // PATCH {} is a 422. Save is already disabled when nothing changed; this is
    // the belt to that pair of braces.
    if (Object.keys(payload).length === 0) {
      setFormError('Nothing has changed yet.');
      return;
    }

    setSubmitting(true);
    try {
      const result = product
        ? await api.updateProduct(product.id, payload)
        : await api.createProduct(payload as ProductCreateInput);
      onSaved(result.data, result.xeroPush);
      onClose();
    } catch (caught) {
      if (caught instanceof ApiError) {
        if (caught.status === 409) {
          // product_code carries a UNIQUE key. The API says so in prose; the
          // form says it next to the input that can actually be fixed.
          setServerErrors({ productCode: caught.message });
        } else {
          /*
           * Split against this form's OWN field list, never against the shape
           * of the path. `unroutable` is everything no input here can carry —
           * the EMPTY-path `.strict()` and "at least one field" refusals, plus
           * any path naming a key this form does not render, the Xero ones
           * included. Rendering it at form level is the only way it is seen at
           * all, and it is never counted as highlighted.
           */
          const { fields, unroutable } = caught.routeFieldErrors(KNOWN_WRITE_KEYS);
          setServerErrors(fields);
          if (unroutable.length > 0) setFormError(unroutable.join(' '));
          else if (Object.keys(fields).length === 0) setFormError(caught.message);
        }
      } else {
        setFormError('Could not reach the server. Check your connection.');
      }
      setSubmitting(false);
    }
  }

  const textProps = (key: TextKey) => ({
    fieldKey: key,
    value: values[key],
    error: errors[key],
    onChange: setField,
  });

  const fieldErrorCount = Object.keys(errors).length;
  const saveDisabled = isEdit ? !isDirty : values.productName.trim() === '';

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      dismissOnBackdrop={false}
      initialFocusRef={nameRef}
      title={product ? `Edit ${product.productName}` : 'New product'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            form={formId}
            variant="primary"
            loading={submitting}
            disabled={saveDisabled}
          >
            {isEdit ? 'Save changes' : 'Create product'}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit} noValidate className="space-y-4">
        <FormErrorSummary formError={formError} fieldErrorCount={fieldErrorCount} />

        <div className="grid gap-3 sm:grid-cols-2">
          <div
            className="sm:col-span-2"
            ref={(node) => {
              nameRef.current = node?.querySelector('input') ?? null;
            }}
          >
            <Field label="Product name" required error={errors.productName}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Input
                  id={id}
                  value={values.productName}
                  maxLength={MAX_LENGTHS.productName}
                  invalid={Boolean(errors.productName)}
                  aria-describedby={describedBy}
                  onChange={(event) => setField('productName', event.target.value)}
                />
              )}
            </Field>
          </div>

          <TextField label="Product code" {...textProps('productCode')} />
          <TextField label="Category" {...textProps('productCategory')} />

          {/*
            Full width: the option labels are product names at real catalogue
            length ("Sales - Business School Festivals: …"), and the difference
            between two of them is in the tail.
          */}
          <div className="sm:col-span-2">
            <Field label="Travel product" error={errors.travelProductId}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Combobox
                  id={id}
                  aria-describedby={describedBy}
                  value={values.travelProductId}
                  onChange={(value) => {
                    // Pin the choice from the list it was made in, BEFORE the
                    // next refetch drops it. '' is the empty option, which
                    // selects nothing and so pins nothing.
                    setTravelSelectionPin(
                      travelProductOptions.find((option) => option.value === value) ?? null,
                    );
                    setField('travelProductId', value);
                  }}
                  options={travelProductOptions}
                  placeholder="No travel product"
                  // Choosing this sends an explicit null, which clears the
                  // association — the only way back from having set one.
                  emptyOptionLabel="No travel product"
                  searchPlaceholder="Search product name…"
                  onSearch={searchTravelProducts}
                  loading={travelLoading && travelProductOptions.length === 0}
                  disabled={submitting}
                  invalid={Boolean(errors.travelProductId)}
                />
              )}
            </Field>
          </div>

          {/*
            The two money fields lead the row so they sit side by side at `sm`
            and up: a recommended discount is only legible against the price it
            comes off, and reading one with the other on the row above is how
            somebody talks themselves into a figure that is most of the price.
            Status moved down one slot to make room; nothing else was re-ordered.
          */}
          <Field label="Unit price" error={errors.unitPrice}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Input
                id={id}
                // `text` with a numeric inputMode, not `type="number"`: the
                // value is an exact decimal string end to end, and a number
                // input hands back a locale-formatted or rounded value.
                inputMode="decimal"
                value={values.unitPrice}
                invalid={Boolean(errors.unitPrice)}
                aria-describedby={describedBy}
                onChange={(event) => setField('unitPrice', event.target.value)}
              />
            )}
          </Field>

          <Field
            label="Recommended discount amount"
            error={errors.recommendedDiscountAmount}
          >
            {({ id, 'aria-describedby': describedBy }) => (
              <Input
                id={id}
                // Same reasoning as the price above, and the same reason there
                // is no `min`/`step` here: this is a decimal string, not a
                // number the browser is allowed to reformat.
                inputMode="decimal"
                value={values.recommendedDiscountAmount}
                invalid={Boolean(errors.recommendedDiscountAmount)}
                aria-describedby={describedBy}
                onChange={(event) => setField('recommendedDiscountAmount', event.target.value)}
              />
            )}
          </Field>

          <Field label="Sold in multiples of" error={errors.quantityStep}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Input
                id={id}
                inputMode="numeric"
                value={values.quantityStep}
                invalid={Boolean(errors.quantityStep)}
                aria-describedby={describedBy}
                onChange={(event) => setField('quantityStep', event.target.value)}
              />
            )}
          </Field>

          <TextField label="Unit" {...textProps('unitLabel')} />

          <Field label="Status" error={errors.productActive}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Select
                id={id}
                value={values.productActive}
                invalid={Boolean(errors.productActive)}
                aria-describedby={describedBy}
                onChange={(event) => setField('productActive', event.target.value)}
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </Select>
            )}
          </Field>

          <Field label="Quantity ordered" error={errors.qtyOrdered}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Input
                id={id}
                inputMode="numeric"
                value={values.qtyOrdered}
                placeholder="0"
                invalid={Boolean(errors.qtyOrdered)}
                aria-describedby={describedBy}
                onChange={(event) => setField('qtyOrdered', event.target.value)}
              />
            )}
          </Field>

          <TextField label="Product owner" {...textProps('productOwner')} />
          <TextField label="Product owner ID" {...textProps('productOwnerId')} />
          {/* Hidden on an edit whose record came from an older API (key absent):
              that API's `.strict()` schema would 422 the key. */}
          {!(product !== undefined && product.xeroSalesAccountCode === undefined) && (
            <TextField label="Xero sales account" {...textProps('xeroSalesAccountCode')} />
          )}

          {/*
            Last row and full width, where this form's sibling `MasterFormModal`
            also puts its flags: a tickbox carries its label inline, so dropped
            into a half-column between two `Field`s it would sit at a different
            vertical rhythm from every label beside it.

            ⚠️ **NO `description` PROP.** `Checkbox` offers one and the standing
            prohibition forbids using it here — what ticking this does is
            recorded at `FormValues.isAddOn` instead. The error line below is a
            different thing and stays: a refusal is one of the four kinds of
            sentence that rule keeps by name.
          */}
          <div className="sm:col-span-2">
            <Checkbox
              id={`${formId}-is-add-on`}
              checked={values.isAddOn === 'true'}
              aria-describedby={errors.isAddOn ? `${formId}-is-add-on-error` : undefined}
              onChange={(event) => setField('isAddOn', String(event.target.checked))}
              label="Its lines get no row of their own on Client Deliverables"
            />
            {/*
              Mirrors `Field`'s own error line, because a bare `Checkbox` is not
              wrapped in one — it carries its own label, and a `Field` around it
              would render a second. Without this a 422 naming `isAddOn` would be
              routed to a control that displays nothing, vanish from the banner,
              and still count towards "fields need attention": the exact failure
              `KNOWN_WRITE_KEYS` is documented against.
            */}
            {errors.isAddOn && (
              <p
                id={`${formId}-is-add-on-error`}
                className="mt-1 text-xs break-words text-danger"
              >
                {errors.isAddOn}
              </p>
            )}
          </div>
        </div>

        {isEdit && <XeroNote product={product} />}
      </form>
    </Modal>
  );
}

/**
 * The Xero columns are owned by the sync and rejected on write, so they are
 * reported rather than offered. Saying "not synced" is more use than leaving
 * the reader to wonder whether the form simply omits them.
 */
function XeroNote({ product }: { product: Product }) {
  /*
   * The two-way Xero Items sync (owner 2026-10-28) reports a real link status;
   * when the API sends it, that is the answer and the legacy sentence below is
   * not. ABSENT = an older API, which keeps the old wording.
   */
  if (product.xeroSync !== undefined) {
    return (
      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs">
        <span className="font-medium text-fg-secondary">Xero</span>
        <ProductXeroBadge product={product} inlineError />
      </div>
    );
  }
  const synced = product.xeroProductId ?? product.xeroProductCode;
  return (
    <p className="text-xs text-fg-muted">
      <span className="font-medium text-fg-secondary">Xero</span>{' '}
      {synced ? (
        <>
          synced as {product.xeroProductCode ?? product.xeroProductId} — set by the sync, not
          editable here.
        </>
      ) : (
        <>not synced yet — these fields are set by the sync.</>
      )}
    </p>
  );
}

interface TextFieldProps {
  fieldKey: TextKey;
  label: string;
  value: string;
  error?: string;
  placeholder?: string;
  onChange: (key: TextKey, value: string) => void;
}

/*
 * `maxLength` comes from the shared table rather than each call site, so the
 * control and the validator can never drift apart.
 */
function TextField({ fieldKey, label, value, error, placeholder, onChange }: TextFieldProps) {
  return (
    <Field label={label} error={error}>
      {({ id, 'aria-describedby': describedBy }) => (
        <Input
          id={id}
          value={value}
          maxLength={MAX_LENGTHS[fieldKey]}
          placeholder={placeholder}
          invalid={Boolean(error)}
          aria-describedby={describedBy}
          onChange={(event) => onChange(fieldKey, event.target.value)}
        />
      )}
    </Field>
  );
}
